import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Clock, Mail, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useKoffieNav } from './KoffieApp';
import { koffieClient } from '../lib/supabase';

// ─── Tokens ───────────────────────────────────────────────────────────────────

const t = {
  bg:     '#f2f0eb',
  white:  '#ffffff',
  text:   '#0c0c0b',
  muted:  '#8a8780',
  body:   '#5b5953',
  lines:  '#dcd9d1',
  accent: '#c75a0a',
  sans:   "'Jost', system-ui, sans-serif",
  serif:  "'Playfair Display', Georgia, serif",
};

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: ${t.bg};
  font-family: ${t.sans};
`;

const Page = styled.div`
  padding: 36px 40px 80px;
  max-width: 880px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: 600px) {
    padding: 24px 20px 64px;
    gap: 20px;
  }
`;

const Anim = styled.div`
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${p => p.$d || '0ms'};
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const GreetingTitle = styled.h1`
  font-family: ${t.serif};
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 900;
  color: ${t.text};
  margin: 0 0 4px;
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

const GreetingSub = styled.p`
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.muted};
  margin: 0;
`;

const TimeTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${t.lines};
  border-radius: 6px;
  padding: 6px 12px;
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 500;
  color: ${t.muted};
  white-space: nowrap;
  flex-shrink: 0;
  background: ${t.white};
`;

const DateSpan = styled.span`
  @media (max-width: 480px) { display: none; }
`;

// ─── Alert banner ─────────────────────────────────────────────────────────────

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(192,57,43,0.06);
  border: 1px solid rgba(192,57,43,0.18);
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(192,57,43,0.10); }
`;

const AlertBadge = styled.div`
  min-width: 22px;
  height: 22px;
  border-radius: 4px;
  background: #c0392b;
  color: #fff;
  font-family: ${t.sans};
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AlertText = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 600;
  color: #c0392b;
`;

const AlertSub = styled.div`
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
  margin-top: 2px;
`;

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabel = styled.div`
  font-family: ${t.sans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${t.muted};
  margin-bottom: 10px;
`;


// ─── Contactverzoeken widget ───────────────────────────────────────────────────

const Card = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1px solid ${t.lines};
`;

const CardTitle = styled.div`
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 600;
  color: ${t.text};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CardLink = styled.button`
  font-family: ${t.sans};
  font-size: 11px;
  font-weight: 500;
  color: ${t.muted};
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
  &:hover { color: ${t.accent}; }
`;

const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid ${t.lines};
  cursor: pointer;
  transition: background 0.12s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${t.bg}; }
`;

const ContactName = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 600;
  color: ${t.text};
`;

const ContactMsg = styled.div`
  font-family: ${t.sans};
  font-size: 12px;
  color: ${t.muted};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 340px;
`;

const ContactMeta = styled.div`
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
  white-space: nowrap;
  flex-shrink: 0;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.$new ? '#c0392b' : t.muted};
  margin-right: 6px;
  vertical-align: middle;
`;

const EmptyState = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.muted};
  padding: 20px 16px;
  opacity: 0.5;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getGreeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Goedemorgen' : h < 18 ? 'Goedemiddag' : 'Goedenavond';
}

function getTime() {
  return new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function getDate() {
  return new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

function fmtDate(str) {
  if (!str) return '';
  const d = new Date(str);
  const now = new Date();
  const diff = Math.floor((now - d) / 60000);
  if (diff < 60) return `${diff} min geleden`;
  if (diff < 1440) return `${Math.floor(diff / 60)} uur geleden`;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function KoffieDashboard() {
  const [time, setTime]           = useState(getTime());
  const [contactCount, setContactCount] = useState(0);
  const [recentContacts, setRecentContacts] = useState([]);
  const navigate = useNavigate();
  const { handleNavigation } = useKoffieNav();

  useEffect(() => {
    const timer = setInterval(() => setTime(getTime()), 10000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    koffieClient
      .from('contact-submissions')
      .select('id, name, message, submitted_at')
      .order('submitted_at', { ascending: false })
      .limit(5)
      .then(({ data }) => {
        setRecentContacts(data ?? []);
        setContactCount(data?.length ?? 0);
      });
  }, []);

  const goContact = () => handleNavigation(() => navigate('/Koffiereparatie/contact'));

  return (
    <Wrap>
      <Page>

        {/* Header */}
        <Anim $d="0ms">
          <HeaderRow>
            <div>
              <GreetingTitle>{getGreeting()}</GreetingTitle>
              <GreetingSub>Welkom op jouw dashboard</GreetingSub>
            </div>
            <TimeTag>
              <Clock size={11} />
              {time}<DateSpan> · {getDate()}</DateSpan>
            </TimeTag>
          </HeaderRow>
        </Anim>

        {/* Alert banner als er onafgehandelde verzoeken zijn */}
        {contactCount > 0 && (
          <Anim $d="40ms">
            <Alert onClick={goContact}>
              <AlertBadge>{contactCount}</AlertBadge>
              <div>
                <AlertText>Onafgehandelde contactverzoeken</AlertText>
                <AlertSub>Klik om te bekijken →</AlertSub>
              </div>
            </Alert>
          </Anim>
        )}

        {/* Recente contactverzoeken */}
        <Anim $d="80ms">
          <SectionLabel>Openstaande contactverzoeken</SectionLabel>
          <Card>
            <CardHeader>
              <CardTitle><Mail size={12} /> Nieuw &amp; bezig</CardTitle>
              <CardLink onClick={goContact}>
                Alles bekijken <ArrowRight size={11} />
              </CardLink>
            </CardHeader>
            {recentContacts.length === 0
              ? <EmptyState>Geen openstaande verzoeken</EmptyState>
              : recentContacts.map(c => (
                <ContactRow key={c.id} onClick={goContact}>
                  <div style={{ minWidth: 0 }}>
                    <ContactName>{c.name}</ContactName>
                    <ContactMsg>{c.message}</ContactMsg>
                  </div>
                  <ContactMeta>{fmtDate(c.submitted_at)}</ContactMeta>
                </ContactRow>
              ))
            }
          </Card>
        </Anim>

      </Page>
    </Wrap>
  );
}
